/* Ayudate · Grilla de disponibilidad */

import React, { useState, useEffect, useRef } from "react";
import { DIAS, HORAS, hLabel } from "../lib/horarios";

/* Tutorías estrictamente virtuales: cada bloque solo tiene dos estados, sin
   sublista que elegir. Negro (predeterminado) es "no disponible"; un clic lo
   pasa a "virtual" y un segundo clic lo vuelve a dejar en negro. */
export function Grilla({ valor, onChange }) {
  /* En celular la grilla de 7 × 16 deja celdas de 40 px: difíciles de acertar.
     Ahí se muestra un día a la vez, con botones grandes.                      */
  const [angosto, setAngosto] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches);
  const [diaVista, setDiaVista] = useState(1);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const cambio = (e) => setAngosto(e.matches);
    mq.addEventListener("change", cambio);
    return () => mq.removeEventListener("change", cambio);
  }, []);

  const alternar = (k) => {
    const copia = { ...valor };
    if (copia[k]) delete copia[k]; else copia[k] = "virtual";
    onChange(copia);
  };

  /* ---------- Escritorio: pintar/borrar varias celdas arrastrando ----------
     Un solo gesto (mousedown → mover sobre otras celdas → mouseup) marca o
     desmarca todas las celdas que toca, en vez de tener que hacer clic una
     por una. El "modo" (marcar o desmarcar) lo decide la PRIMERA celda del
     arrastre, según su estado en ese instante, y se aplica igual a todas las
     demás celdas que se crucen en el mismo gesto — así arrastrar sobre una
     mezcla de celdas marcadas y sin marcar no da un resultado ambiguo.
     Vive en un ref (no en estado) porque no necesita volver a renderizar
     nada por su cuenta: cada celda que toca ya dispara su propio onChange. */
  const arrastreRef = useRef(null);

  const marcadaEn = (k) => !!valor[k];

  const aplicarCelda = (k, marcar) => {
    if (marcar === marcadaEn(k)) return;   // ya está como se quiere dejar
    const copia = { ...valor };
    if (marcar) copia[k] = "virtual"; else delete copia[k];
    onChange(copia);
  };

  const iniciarArrastre = (k) => (e) => {
    if (e.button !== 0) return;   // solo botón principal del mouse (o toque)
    const marcar = !marcadaEn(k);
    arrastreRef.current = { marcar, tocadas: new Set([k]) };
    aplicarCelda(k, marcar);
  };

  const seguirArrastre = (k) => () => {
    const d = arrastreRef.current;
    if (!d || d.tocadas.has(k)) return;
    d.tocadas.add(k);
    aplicarCelda(k, d.marcar);
  };

  /* El mouseup puede caer en cualquier parte (incluso fuera de la grilla si
     el usuario suelta afuera): se escucha en window, no en cada celda, para
     no perder ese caso. */
  useEffect(() => {
    const terminarArrastre = () => { arrastreRef.current = null; };
    window.addEventListener("pointerup", terminarArrastre);
    window.addEventListener("pointercancel", terminarArrastre);
    return () => {
      window.removeEventListener("pointerup", terminarArrastre);
      window.removeEventListener("pointercancel", terminarArrastre);
    };
  }, []);

  /* El clic normal (mouse o toque) ya lo resuelve iniciarArrastre() de
     arriba en el pointerdown; este onClick queda solo para la activación
     por teclado (Enter/Espacio con la celda enfocada), que no dispara
     pointerdown — se distingue por detail === 0, que es como el navegador
     marca un click "sintético" de teclado en vez de uno real del mouse. */
  const clicPorTeclado = (k) => (e) => {
    if (e.detail === 0) alternar(k);
  };

  const marcadosDe = (k) => HORAS.filter((h) => valor[`${k}-${h}`]).length;
  const marcados = Object.keys(valor).length;

  /* ---------- Celular: un día a la vez ---------- */
  if (angosto) {
    return (
      <div className="gwrap">
        <div className="gdias" role="tablist" aria-label="Día de la semana">
          {DIAS.map((d) => {
            const n = marcadosDe(d.k);
            return (
              <button type="button" key={d.k} role="tab" aria-selected={diaVista === d.k}
                className={`gdia ${diaVista === d.k ? "on" : ""}`}
                onClick={() => setDiaVista(d.k)}>
                {d.lb}
                {n > 0 && <i>{n}</i>}
              </button>
            );
          })}
        </div>

        <div className="ghoras" role="tabpanel"
          aria-label={`Horas del ${DIAS.find((d) => d.k === diaVista).lb}`}>
          {HORAS.map((h) => {
            const k = `${diaVista}-${h}`, v = !!valor[k];
            return (
              <button type="button" key={k} className={`ghora ${v ? "virtual" : ""}`}
                aria-pressed={v} onClick={() => alternar(k)}>
                <span className="ghh2">{hLabel(h)}</span>
                <span className="ghv">{v ? "Virtual" : "No disponible"}</span>
              </button>
            );
          })}
        </div>

        <div className="gleg">
          <div className="gleg-items">
            <span><i className="gsw vac" />no disponible</span>
            <span><i className="gsw virtual" />virtual</span>
          </div>
          {marcados > 0 && (
            <button type="button" className="limpiar" onClick={() => onChange({})}>
              Limpiar todo
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ---------- Escritorio: la grilla completa ---------- */
  return (
    <div className="gwrap">
      <div className="grid-h" style={{ gridTemplateColumns: `44px repeat(${DIAS.length}, 1fr)` }}>
        <div />
        {DIAS.map((d) => <div className="gdd" key={d.k}>{d.lb}</div>)}
        {HORAS.map((h) => (
          <React.Fragment key={h}>
            <div className="ghh">{hLabel(h)}</div>
            {DIAS.map((d) => {
              const k = `${d.k}-${h}`, v = !!valor[k];
              return (
                <div className="gcell" key={k}>
                  <button type="button" className={`gcel ${v ? "virtual" : ""}`}
                    aria-pressed={v}
                    aria-label={`${d.lb} ${hLabel(h)}: ${v ? "virtual" : "no disponible"}`}
                    onPointerDown={iniciarArrastre(k)}
                    onPointerEnter={seguirArrastre(k)}
                    onClick={clicPorTeclado(k)}>
                    {v ? "V" : ""}
                  </button>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="gleg">
        <div className="gleg-items">
          <span><i className="gsw vac" />no disponible</span>
          <span><i className="gsw virtual" />V · virtual</span>
        </div>
        {marcados > 0 && (
          <button type="button" className="limpiar" onClick={() => onChange({})}>
            Limpiar todo
          </button>
        )}
      </div>
    </div>
  );
}
